import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  documents_needed: { label: "Documents Needed", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  preparing: { label: "Preparing", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  ready_for_review: { label: "Ready for Review", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  awaiting_authorization: { label: "Awaiting Authorization", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  sent_to_preparer: { label: "Sent to Preparer", cls: "bg-violet-50 text-violet-800 border-violet-200" },
  filed: { label: "Filed", cls: "bg-teal-50 text-teal-800 border-teal-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-800 border-red-200" },
  action_required: { label: "Action Required", cls: "bg-red-600 text-white border-red-600" },
};

export const cad = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n || 0));

export const errToast = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export function TaxShell({ children, testId }) {
  return (
    <div className="tax-light -mx-2 md:-mx-4 px-4 md:px-8 py-6 md:py-8 animate-fade-up" data-testid={testId}>
      {children}
    </div>
  );
}

export function StatusBadge({ status, testId }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function Field({ label, id, hint, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input id={id} data-testid={id} className="bg-white" {...props} />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

export function NativeSelect({ label, id, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      {label && <Label htmlFor={id} className="text-xs font-semibold text-slate-600">{label}</Label>}
      <select id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md border border-input px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Toggle({ id, label, checked, onChange, hint }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 py-2 cursor-pointer">
      <input id={id} data-testid={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-red-600" />
      <span className="text-sm text-slate-800">
        {label}
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

export function Section({ title, subtitle, children, right, testId }) {
  return (
    <section className="tax-card p-5 md:p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-base md:text-lg font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Disclaimer() {
  return (
    <p className="text-xs text-slate-500 leading-relaxed" data-testid="tax-disclaimer">
      MyPayAlert prepares an <strong>estimate</strong> from the information you provide. It is not a filed or assessed return
      and no refund is guaranteed. MyPayAlert is not a CRA EFILE service. Your return is filed only by an authorized tax preparer
      using CRA-certified software.
    </p>
  );
}
